import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

/**
 * VoiceInput - Botón de entrada por voz para el chat de ALMA.
 * Usa la Web Speech API nativa (webkitSpeechRecognition).
 */

// Variable de control: true = envía automático al terminar de hablar
// false = solo inserta en el input y deja que el usuario edite/envíe manualmente
const AUTO_SEND_VOICE = true;
const LISTENING_TIMEOUT_MS = 8000;

const VoiceInput = ({ onTranscript, onAutoSend, disabled }) => {
  const [startError, setStartError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [debugInfo, setDebugInfo] = useState("Tocá para hablar");

  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const finalTextRef = useRef("");
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, []);

  const cleanup = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    try {
      recognitionRef.current?.abort();
    } catch (e) {}
    recognitionRef.current = null;
  };

  const finishListening = (text) => {
    cleanup();
    if (!isMountedRef.current) return;

    const finalText = text?.trim();
    if (finalText) {
      onTranscript(finalText);
      if (AUTO_SEND_VOICE) {
        onAutoSend?.(finalText);
      }
    }

    finalTextRef.current = "";
    setIsListening(false);
    setIsProcessing(false);
    setDebugInfo(finalText ? `Enviado: "${finalText}"` : "Sin texto");
  };

  const doStartListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setStartError("No soportado");
      setIsProcessing(false);
      return;
    }

    setStartError(null);
    setIsProcessing(true);
    setIsListening(true);
    setDebugInfo("Escuchando...");
    finalTextRef.current = "";

    const recognition = new SpeechRecognition();
    recognition.lang = "es-CL";
    recognition.continuous = false;
    recognition.interimResults = true;
    recognitionRef.current = recognition;

    recognition.onstart = () => {
      if (!isMountedRef.current) return;
      setIsListening(true);
      setDebugInfo("Escuchando...");
    };

    recognition.onresult = (event) => {
      if (!isMountedRef.current) return;
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      if (final) {
        finalTextRef.current += final;
      }
      setDebugInfo(`F="${finalTextRef.current}" | I="${interim}"`);
    };

    recognition.onerror = (event) => {
      if (!isMountedRef.current) return;
      console.error("[VoiceInput] Speech error:", event.error);

      // Error "aborted" = el diálogo de permisos de iOS cortó la sesión.
      // Limpiamos todo y dejamos listo para que el usuario toque de nuevo.
      cleanup();
      setIsListening(false);
      setIsProcessing(false);
      recognitionRef.current = null;

      if (event.error === "aborted") {
        setDebugInfo("Permiso requerido. Tocá de nuevo.");
        return;
      }

      if (event.error === "not-allowed") {
        setStartError("Permiso de micrófono denegado");
      } else if (event.error === "no-speech") {
        setStartError("No se detectó voz");
      } else {
        setStartError(`Error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      if (!isMountedRef.current) return;
      setIsListening(false);
      recognitionRef.current = null;
      finishListening(finalTextRef.current);
    };

    try {
      recognition.start();
    } catch (err) {
      console.error("[VoiceInput] Error start:", err);
      setStartError("No se pudo iniciar");
      setIsListening(false);
      setIsProcessing(false);
      recognitionRef.current = null;
    }

    // Timeout de seguridad
    timeoutRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      setDebugInfo("Timeout seguridad");
      try {
        recognition.stop();
      } catch (e) {
        finishListening(finalTextRef.current);
      }
    }, LISTENING_TIMEOUT_MS);
  };

  const toggleListening = () => {
    if (isListening || isProcessing) {
      setDebugInfo("Deteniendo...");
      try {
        recognitionRef.current?.stop();
      } catch (e) {
        finishListening(finalTextRef.current);
      }
    } else {
      doStartListening();
    }
  };

  const browserSupportsSpeechRecognition =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  if (!browserSupportsSpeechRecognition) {
    return (
      <VoiceWrapper>
        <VoiceButton disabled title="Tu navegador no soporta reconocimiento de voz">
          🎤
        </VoiceButton>
      </VoiceWrapper>
    );
  }

  return (
    <VoiceWrapper>
      <VoiceButton
        onClick={toggleListening}
        disabled={disabled || isProcessing}
        $listening={isListening}
        $hasError={Boolean(startError)}
        title={startError || (isListening ? "Escuchando... toca para detener" : "Hablar para escribir")}
      >
        {isListening ? (
          <>
            <Pulse />
            Escuchando
          </>
        ) : startError ? (
          "⚠️"
        ) : (
          "🎤"
        )}
      </VoiceButton>
      {startError && <ErrorTooltip>{startError}</ErrorTooltip>}
      <DebugPanel>{debugInfo}</DebugPanel>
    </VoiceWrapper>
  );
};

export default VoiceInput;

/* ========== ESTILOS ========== */

const VoiceWrapper = styled.div`
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-self: stretch;
  align-items: center;
`;

const VoiceButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: ${({ $listening, $hasError }) =>
    $hasError ? "#fee2e2" : $listening ? "#dc2626" : "#f3f4f6"};
  color: ${({ $listening, $hasError }) =>
    $hasError ? "#991b1b" : $listening ? "#fff" : "#374151"};
  border: none;
  border-radius: 10px;
  padding: 0 0.8rem;
  font-size: 0.95rem;
  cursor: pointer;
  align-self: stretch;
  min-height: 44px;
  transition: background 0.2s ease, color 0.2s ease;
  white-space: nowrap;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover:not(:disabled) {
    background: ${({ $listening, $hasError }) =>
      $hasError ? "#fecaca" : $listening ? "#b91c1c" : "#e5e7eb"};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ErrorTooltip = styled.span`
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #991b1b;
  color: #fff;
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 10;
`;

const Pulse = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  background: #fff;
  border-radius: 50%;
  animation: pulse 1s infinite;

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.4);
      opacity: 0.6;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const DebugPanel = styled.span`
  font-size: 10px;
  color: #999;
  margin-top: 2px;
  text-align: center;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
